import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

import { PeriodKey } from '../constants';

dayjs.extend(quarterOfYear);

export interface DateRange {
  start: string;
  end: string;
}

const fmt = (d: dayjs.Dayjs): string => d.format('YYYY-MM-DD');

export const periodRange = (period: PeriodKey): DateRange => {
  const now = dayjs();
  switch (period) {
    case 'today':
      return { start: fmt(now.startOf('day')), end: fmt(now.endOf('day')) };
    case 'month':
      return { start: fmt(now.startOf('month')), end: fmt(now.endOf('month')) };
    case 'quarter':
      return {
        start: fmt(now.startOf('quarter')),
        end: fmt(now.endOf('quarter')),
      };
    case 'year':
    default:
      // capped at 12 months back to avoid unbounded API payloads — when
      // the backend exposes an aggregated endpoint this can be lifted.
      return {
        start: fmt(now.subtract(11, 'month').startOf('month')),
        end: fmt(now.endOf('day')),
      };
  }
};

export const previousRange = (period: PeriodKey): DateRange => {
  const now = dayjs();
  switch (period) {
    case 'today': {
      const y = now.subtract(1, 'day');
      return { start: fmt(y.startOf('day')), end: fmt(y.endOf('day')) };
    }
    case 'month': {
      const prev = now.subtract(1, 'month');
      return {
        start: fmt(prev.startOf('month')),
        end: fmt(prev.endOf('month')),
      };
    }
    case 'quarter': {
      const prev = now.subtract(1, 'quarter');
      return {
        start: fmt(prev.startOf('quarter')),
        end: fmt(prev.endOf('quarter')),
      };
    }
    case 'year':
    default: {
      // matches the 12-month cap used in periodRange('year')
      const prev = now.subtract(12, 'month');
      return {
        start: fmt(prev.subtract(11, 'month').startOf('month')),
        end: fmt(prev.endOf('month')),
      };
    }
  }
};

export const last12Months = (): DateRange => {
  const now = dayjs();
  return {
    start: fmt(now.subtract(11, 'month').startOf('month')),
    end: fmt(now.endOf('day')),
  };
};

export const monthBucketsLast12 = (): { key: string; label: string }[] => {
  const now = dayjs();
  return Array.from({ length: 12 }, (_, i) => {
    const m = now.subtract(11 - i, 'month');
    return {
      key: m.format('YYYY-MM'),
      label: m.format('MMM/YY'),
    };
  });
};
